import { groupBy, zip } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { TrussEnvelope } from '@paragon/contract-library';
import { distanceEquals, distanceGreaterThan } from '@paragon/geometry-library';
import {
  calculateTrussSolid,
  calculateTrussSolidWithCache,
  getEnvelopeSegment,
  TrussSolidCalculationDependencies,
} from '@paragon/layout';
import { groupTransitively, pairwise } from '@paragon/utilities';

import { RootState } from '../../../store';
import { NULL_LEVEL_GUID_KEY } from '../../../store/selectors/layoutDataSelectors';
import { getTrussSolidCalculationDependencies } from './trussSolidSelectors';

export const calculateTrussEnvelopeBaseElevationForCapSet = createSelector(
  (envelope: TrussEnvelope) => envelope,
  (envelope, dependencies: TrussSolidCalculationDependencies) => dependencies,

  (envelope, dependencies) => {
    const solidTouchingBase =
      envelope.containerLevel === 'BaseLevel' || envelope.containerLevel === 'NotCapped'
        ? calculateTrussSolidWithCache(envelope, dependencies).ok
        : calculateTrussSolid({ ...envelope, containerLevel: 'NotCapped' }, dependencies).ok;

    if (solidTouchingBase) {
      return Math.min(...solidTouchingBase.vertices.map(({ z }) => z));
    }

    return 0;
  },
);

/**
 * Returns a record keyed by envelope guid where each value contains two properties:
 * - `capSetState`: the `CapSetState` for the cap set of which this envelope is a part
 * - `involvedEnvelopeGuids`: the guids of every truss envelope which comprise the cap set
 */
export const calculateTrussEnvelopeCapSetGroupings = createSelector(
  (state: RootState) => state.layout.trussEnvelopes,
  getTrussSolidCalculationDependencies,
  (trussEnvelopes, dependencies) => {
    // Envelopes in different levels cannot be grouped

    const capGroupsWithInvolvedEnvelopes = Object.values(
      groupBy(Object.values(trussEnvelopes), ({ levelGuid }) => levelGuid ?? NULL_LEVEL_GUID_KEY),
    )
      .flatMap((envelopesInLevel) => {
        // Envelopes in the same level with overlapping geometries can be grouped
        const envelopesWithDefiningLines = envelopesInLevel.map((envelope) => ({
          envelope,
          segment: getEnvelopeSegment(envelope),
        }));
        const overlappingEnvelopes = groupTransitively(
          envelopesWithDefiningLines,
          ({ segment: segment1 }, { segment: segment2 }) =>
            segment1 != null &&
            segment2 != null &&
            (segment1.containsSegment(segment2) || segment2.containsSegment(segment1)),
        ).map((group) => group.map(({ envelope }) => envelope));
        return overlappingEnvelopes;
      })
      .flatMap((group) => {
        // If multiple envelopes are Cap2 or Base/Uncapped, fail early by splitting the group into singletons
        if (
          group.filter(({ containerLevel }) => containerLevel === 'BaseLevel').length > 1 ||
          group.filter(({ containerLevel }) => containerLevel === 'CapLevelTwo').length > 1 ||
          (group.some(({ containerLevel }) => containerLevel === 'NotCapped') && group.length > 1)
        )
          return group.map((element) => [element]);

        return [group];
      })
      // In each group, find lower elevation (and cap status) and higher elevation (and cap status)
      .flatMap((group) => {
        const groupWithElevationInformation = group
          .map((envelope) => {
            let bottomElevation = 0;
            if (envelope.containerLevel === 'CapLevelOne') bottomElevation = envelope.capLevelOne;
            else if (envelope.containerLevel === 'CapLevelTwo') bottomElevation = envelope.capLevelTwo;
            else {
              const solid = calculateTrussSolidWithCache(envelope, dependencies).ok;
              if (solid) bottomElevation = Math.min(...solid.vertices.map(({ z }) => z));
            }

            let topElevation = 0;
            if (envelope.containerLevel === 'BaseLevel') topElevation = envelope.capLevelOne;
            else if (envelope.containerLevel === 'CapLevelOne') topElevation = envelope.capLevelTwo;
            else {
              const solid = calculateTrussSolidWithCache(envelope, dependencies).ok;
              if (solid) topElevation = Math.max(...solid.vertices.map(({ z }) => z));
            }

            return { envelope, bottomElevation, topElevation };
          })
          // Sort by lower elevation and flatten into preliminary cap set information
          .toSorted((a, b) => a.bottomElevation - b.bottomElevation);

        if (
          // The levels can't intersect each other
          pairwise(groupWithElevationInformation).some(([lower, higher]) =>
            distanceGreaterThan(lower.topElevation, higher.bottomElevation),
          ) ||
          // If there is an envelope only capped on the top, it must be the bottom envelope
          (groupWithElevationInformation[0].envelope.containerLevel !== 'BaseLevel' &&
            groupWithElevationInformation.some(({ envelope }) => envelope.containerLevel === 'BaseLevel')) ||
          // If there is an envelope only capped on the bottom, it must be the top envelope
          (groupWithElevationInformation[groupWithElevationInformation.length - 1].envelope.containerLevel !==
            'CapLevelTwo' &&
            groupWithElevationInformation.some(({ envelope }) => envelope.containerLevel === 'CapLevelTwo'))
        )
          // If any of the above conditions hold, split this group into singletons
          return groupWithElevationInformation.map((element) => [element]);
        else return [groupWithElevationInformation];
      })
      // Build the CapSetState for each group of envelopes that comprise a cap set
      .map((capSetGroup): { capSetState: CapSetState; involvedEnvelopeGuids: readonly string[] } => {
        if (capSetGroup[0].envelope.containerLevel === 'NotCapped') {
          // This is a special case because NotCapped envelopes are always singletons
          const envelope = capSetGroup[0];
          return {
            capSetState: { baseElevation: envelope.bottomElevation, generateBase: true, additionalLevels: [] },
            involvedEnvelopeGuids: [capSetGroup[0].envelope.guid],
          };
        }

        let baseElevation: number;
        let generateBase: boolean;
        const additionalLevelsAbsolute: CapSetLevel[] = [];
        // Build base level (if missing) and first envelope level
        if (capSetGroup[0].envelope.containerLevel === 'BaseLevel') {
          generateBase = true;
          baseElevation = capSetGroup[0].bottomElevation;
        } else {
          generateBase = false;
          baseElevation = calculateTrussEnvelopeBaseElevationForCapSet(capSetGroup[0].envelope, dependencies);
          additionalLevelsAbsolute.push({ elevation: capSetGroup[0].bottomElevation, generate: true });
        }
        // Build subsequent envelope levels with preceding intermediate levels (if needed)
        for (const [previousLevel, currentLevel] of pairwise(capSetGroup)) {
          // If the elevations don't match, we need to create an intermediate ungenerated level
          if (!distanceEquals(previousLevel.topElevation, currentLevel.bottomElevation)) {
            additionalLevelsAbsolute.push({ elevation: previousLevel.topElevation, generate: false });
          }

          // Then create a generated level for currentLevel
          additionalLevelsAbsolute.push({ elevation: currentLevel.bottomElevation, generate: true });
        }
        // Build top level (if missing)
        const lastLevel = capSetGroup[capSetGroup.length - 1];
        if (lastLevel.envelope.containerLevel !== 'CapLevelTwo') {
          additionalLevelsAbsolute.push({ elevation: lastLevel.topElevation, generate: false });
        }

        // Elevation is relative to the previous elevation, so perform that conversion
        const additionalLevels = additionalLevelsAbsolute.map((level, index) => {
          const previousLevelElevation = index === 0 ? baseElevation : additionalLevelsAbsolute[index - 1].elevation;
          return { ...level, elevation: level.elevation - previousLevelElevation };
        });

        return {
          capSetState: { baseElevation, generateBase, additionalLevels },
          involvedEnvelopeGuids: capSetGroup.map(({ envelope }) => envelope.guid),
        };
      });

    const capGroupsByEnvelopeGuid: Record<string, (typeof capGroupsWithInvolvedEnvelopes)[number]> = {};
    for (const capGroupWithInvolvedEnvelopes of capGroupsWithInvolvedEnvelopes) {
      for (const involvedEnvelopeGuid of capGroupWithInvolvedEnvelopes.involvedEnvelopeGuids) {
        capGroupsByEnvelopeGuid[involvedEnvelopeGuid] = capGroupWithInvolvedEnvelopes;
      }
    }
    return capGroupsByEnvelopeGuid;
  },
);

export interface CapSetState {
  readonly baseElevation: number;
  readonly generateBase: boolean;
  readonly additionalLevels: readonly CapSetLevel[];
}

export interface CapSetLevel {
  readonly elevation: number;
  readonly generate: boolean;
}

export function capSetStatesAreEqual(state1: CapSetState, state2: CapSetState) {
  if (state1 === state2) return true;

  return (
    distanceEquals(state1.baseElevation, state2.baseElevation) &&
    state1.generateBase === state2.generateBase &&
    state1.additionalLevels.length === state2.additionalLevels.length &&
    zip(state1.additionalLevels, state2.additionalLevels).every(
      ([level1, level2]) =>
        level1 && level2 && distanceEquals(level1.elevation, level2.elevation) && level1.generate === level2.generate,
    )
  );
}

export function generateCapSetProperties(
  state: CapSetState,
): { properties: Partial<TrussEnvelope>; capIndex: number }[] {
  if (state.additionalLevels.length === 0) {
    return state.generateBase
      ? [
          {
            properties: { containerLevel: 'NotCapped', capLevelOne: 0, capLevelTwo: 0, elevation: state.baseElevation },
            capIndex: 0,
          },
        ]
      : [];
  }

  let capIndex = 0;
  let currentBottom = state.baseElevation;
  let currentTop = state.additionalLevels[0].elevation + currentBottom;
  const properties: ReturnType<typeof generateCapSetProperties> = [];
  if (state.generateBase) {
    properties.push({
      properties: {
        containerLevel: 'BaseLevel',
        capLevelOne: currentTop,
        capLevelTwo: 0,
        elevation: state.baseElevation,
      },
      capIndex,
    });
  }

  const defaultCapLevelProperties: Partial<TrussEnvelope> = {
    leftBevelCut: null,
    rightBevelCut: null,
    leftEndConnectionOverride: null,
    rightEndConnectionOverride: null,
    leftOverhang: 0,
    rightOverhang: 0,
    bottomCuts: [],
    topCuts: [],
  };

  for (const [level, nextLevel] of pairwise([...state.additionalLevels, null])) {
    currentBottom = currentTop;
    currentTop = nextLevel == null ? 0 : currentBottom + nextLevel.elevation;
    if (!level!.generate) continue;
    capIndex++;

    if (nextLevel == null) {
      properties.push({
        properties: {
          ...defaultCapLevelProperties,
          containerLevel: 'CapLevelTwo',
          capLevelOne: 0,
          capLevelTwo: currentBottom,
          elevation: currentBottom,
        },
        capIndex,
      });
    } else {
      properties.push({
        properties: {
          ...defaultCapLevelProperties,
          containerLevel: 'CapLevelOne',
          capLevelOne: currentBottom,
          capLevelTwo: currentTop,
          elevation: currentBottom,
        },
        capIndex,
      });
    }
  }

  return properties;
}
